import { create } from 'zustand';
import apiClient from '../lib/api';

export interface TrainingModule {
    id: string;
    tenantId: string;
    title: string;
    description: string;
    durationMinutes: number;
    isMandatory: boolean;
    type: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface TrainingState {
    modules: TrainingModule[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchModules: (filters?: any) => Promise<void>;
    createModule: (data: Partial<TrainingModule>) => Promise<void>;
    updateModule: (id: string, data: Partial<TrainingModule>) => Promise<void>;
    deleteModule: (id: string) => Promise<void>;
}

export const useTrainingStore = create<TrainingState>((set, get) => ({
    modules: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchModules: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/training', { params: filters });
            const { data, pagination } = response.data;
            set({ modules: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch training modules', isLoading: false });
        }
    },
    createModule: async (data: Partial<TrainingModule>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/people/training', data);
            const newModule = response.data;
            set((state) => ({
                modules: [newModule, ...state.modules],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create training module', isLoading: false });
            throw error;
        }
    },
    updateModule: async (id: string, data: Partial<TrainingModule>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/people/training/${id}`, data);
            const updatedModule = response.data;
            set((state) => ({
                modules: state.modules.map((o) => (o.id === id ? updatedModule : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update training module', isLoading: false });
            throw error;
        }
    },
    deleteModule: async (id: string) => {
        set({ error: null });
        try {
            await apiClient.delete(`/people/training/${id}`);
            set((state) => ({
                modules: state.modules.filter((m) => m.id !== id),
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete training module' });
            throw error;
        }
    },
}));
